import React, { useEffect, useState } from 'react';
import { Flame, X } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { fastingApi } from '../fastingApi';
import './FastingInvites.css';

/**
 * "You signed up to fast with Oakdale Board — Join" — the one-tap join for
 * someone who signed up on a public fasting page and has since made an
 * account with the same (verified) email.
 *
 * A public sign-up is the organizer's invitation, so this joins the community
 * whatever its visibility: no request to approve, no hidden community to find.
 * The server holds that rule (POST /api/fasting/invitations/:programId/join);
 * this only offers it.
 *
 * Mounted on the Home feed, the Communities list and a community's join page
 * (`communityId` narrows it to that community). It renders nothing at all
 * until there is an invitation, so on the feed — where almost every member
 * has none — it costs one small request and no layout shift.
 *
 * Joining reloads the page into the fast: the community payload, the tabs and
 * the feature gates were all loaded for a non-member, and a full load is the
 * one way to be sure none of them is stale.
 */
const DISMISS_KEY = (id) => `ignite-fast-invite-dismissed:${id}`;

function isDismissed(id) {
  try { return localStorage.getItem(DISMISS_KEY(id)) === '1'; } catch { return false; }
}

export default function FastingInvites({ communityId = null }) {
  const { t } = useTranslation('fasting');
  const [invites, setInvites] = useState([]);
  const [busy, setBusy]       = useState(null);
  const [error, setError]     = useState('');

  useEffect(() => {
    let alive = true;
    // Promise.resolve: a screen test that stubs the api client returns
    // undefined here, and the feed must not crash over an optional card.
    Promise.resolve(fastingApi.invitations())
      .then((r) => { if (alive) setInvites(r?.invitations || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const shown = invites.filter((i) => (!communityId || i.communityId === communityId)
    && (communityId || !isDismissed(i.programId)));
  if (!shown.length) return null;

  async function join(inv) {
    setBusy(inv.programId); setError('');
    try {
      const r = await fastingApi.joinInvitation(inv.programId);
      window.location.assign(`/communities/${r.communityId}/fasting/${r.programId}`);
    } catch {
      setError(t('invite.error'));
      setBusy(null);
    }
  }

  function dismiss(inv) {
    try { localStorage.setItem(DISMISS_KEY(inv.programId), '1'); } catch { /* private mode */ }
    setInvites((cur) => cur.filter((i) => i.programId !== inv.programId));
  }

  return (
    <div className="fsi-list">
      {shown.map((inv) => (
        <div key={inv.programId} className="fsi-card" role="region" aria-label={t('invite.title', inv.communityName)}>
          <span className="fsi-icon" aria-hidden="true"><Flame size={18} /></span>
          <div className="fsi-text">
            <strong className="fsi-title">{t('invite.title', inv.communityName)}</strong>
            <span className="fsi-body">{t('invite.body', inv.programTitle, inv.shiftCount)}</span>
            {error && busy === null && <span className="fsi-error">{error}</span>}
          </div>
          <div className="fsi-actions">
            <button className="btn btn-primary fsi-join" disabled={busy === inv.programId} onClick={() => join(inv)}>
              {busy === inv.programId ? t('invite.joining') : t('invite.join')}
            </button>
            {!communityId && (
              <button type="button" className="fsi-dismiss" aria-label={t('invite.dismiss')} title={t('invite.dismiss')}
                onClick={() => dismiss(inv)}>
                <X size={16} />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
